import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram } from 'lucide-react';

const footerLinks = {
  programs: [
    { name: 'Basketball Training', path: '/services' },
    { name: 'Swimming Programs', path: '/services' },
    { name: 'Strength & Conditioning', path: '/services' },
    { name: 'Training Packages', path: '/contact' },
  ],
  quickLinks: [
    { name: 'About Us', path: '/about' },
    { name: 'Book Session', path: '/services' },
    { name: 'Contact', path: '/contact' },
  ],
};

export default function Footer() {
  return (
    <footer className="bg-background-secondary dark:bg-background-secondary light:bg-white border-t border-glass-border dark:border-glass-border light:border-gray-200 mt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Brand Section */}
          <div className="space-y-4">
            <motion.h3
              whileHover={{ scale: 1.02 }}
              className="text-xl font-bold bg-gradient-to-r from-primary-orange to-primary-light bg-clip-text text-transparent"
            >
              Better Together Training
            </motion.h3>
            <p className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 leading-relaxed">
              Columbus, Ohio's premier sports coaching and skills development center. We transform athletes through expert basketball, swimming, and strength training.
            </p>
            <div className="flex gap-3">
              <motion.a
                href="https://instagram.com/tootall614"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-primary-orange to-primary-light text-white hover:shadow-neon-orange transition-shadow"
                title="Jalen - @tootall614"
              >
                <Instagram className="w-5 h-5" />
              </motion.a>
              <motion.a
                href="https://instagram.com/sukisinclair.robinson"
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-gradient-to-r from-primary-orange to-primary-light text-white hover:shadow-neon-orange transition-shadow"
                title="Sinclair - @sukisinclair.robinson"
              >
                <Instagram className="w-5 h-5" />
              </motion.a>
            </div>
          </div>

          {/* Programs */}
          <div>
            <h4 className="font-semibold mb-4 text-white dark:text-white light:text-gray-900">Programs</h4>
            <ul className="space-y-2">
              {footerLinks.programs.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 hover:text-primary-light transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold mb-4 text-white dark:text-white light:text-gray-900">Quick Links</h4>
            <ul className="space-y-2">
              {footerLinks.quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.path}
                    className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 hover:text-primary-light transition-colors"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold mb-4 text-white dark:text-white light:text-gray-900">Training Location</h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-primary-light flex-shrink-0 mt-0.5" />
                <div>
                  <a
                    href="mailto:mr.robinson3704@gmail.com"
                    className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 hover:text-primary-light transition-colors break-all block"
                  >
                    mr.robinson3704@gmail.com
                  </a>
                  <a
                    href="mailto:sinclair_hae@yahoo.com"
                    className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 hover:text-primary-light transition-colors break-all block mt-1"
                  >
                    sinclair_hae@yahoo.com
                  </a>
                </div>
              </li>
              <li className="flex items-center space-x-3">
                <Phone className="w-5 h-5 text-primary-light flex-shrink-0" />
                <a
                  href="tel:614-869-7812"
                  className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600 hover:text-primary-light transition-colors"
                >
                  (614) 869-7812
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-primary-light flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400 dark:text-gray-400 light:text-gray-600">Columbus, Ohio</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-glass-border dark:border-glass-border light:border-gray-300">
          <div className="text-center space-y-2">
            <p className="text-sm text-gray-500 dark:text-gray-500 light:text-gray-600">
              &copy; 2026 Better Together LLC. All rights reserved. | Columbus Ohio Sports Coaching & Skills Development | Training Champions Since 2016
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-600 light:text-gray-500">
              Site Design{' '}
              <a
                href="https://xigusa.com/solutions/website-design"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-light hover:text-primary-orange transition-colors underline"
              >
                XIGUSA
              </a>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
